export type GoalType = "fat_loss" | "maintain" | "muscle_gain";
export type DietPattern = "omnivore" | "vegetarian" | "vegan";
export type TrainingFrequency = "0" | "1-2" | "3-4" | "5+";
export type Confidence = "high" | "medium" | "low";
export type GuardStatus = "low" | "on_track" | "strong" | "unknown";
export type MealStatus = "queued" | "processing" | "needs_confirmation" | "ready" | "failed";
export type JobType = "meal_analysis" | "plan_extract";

export interface Range {
  min: number;
  max: number;
}

export const poundsToKilograms = (pounds: number): number => pounds * 0.45359237;

export interface ProfileInput {
  weightKg: number;
  targetWeightKg?: number;
  goalType: GoalType;
  dietPattern: DietPattern;
  trainingFrequency: TrainingFrequency;
  mealsPerDay: 2 | 3 | 4;
  excludedFoods: string[];
  safetyFlags: string[];
  adultConfirmed: boolean;
  privacyConsent: boolean;
  wellnessConsent: boolean;
  timezone: string;
  unitSystem: "metric" | "imperial";
}

export interface TargetProfile {
  id: string;
  protein: Range;
  fiber: Range;
  source: "general_wellness" | "imported_plan" | "manual";
  ruleVersion: string;
  reviewStatus: "development_only" | "professionally_reviewed";
  confirmedAt: string | null;
  requiresProfessionalTarget: boolean;
}

export interface FoodItemEstimate {
  name: string;
  portionDescription: string;
  portionG: Range;
  proteinG: Range;
  fiberG: Range;
  confidence: number;
}

export interface MealAnalysis {
  items: FoodItemEstimate[];
  mealProteinG: Range;
  mealFiberG: Range;
  confidence: Confidence;
  hiddenIngredientRisks: string[];
  needsConfirmation: boolean;
  confirmationQuestion: string | null;
}

export interface OneFoodFix {
  food: string;
  portion: string;
  reason: string;
  proteinG: Range;
  fiberG: Range;
}

export interface MealResult extends MealAnalysis {
  proteinStatus: GuardStatus;
  fiberStatus: GuardStatus;
  oneFoodFix: OneFoodFix | null;
  modelVersion: string;
  promptVersion: string;
  ruleVersion: string;
}

export interface PlanExtraction {
  planTitle: string;
  explicitProteinTargetG: Range | null;
  explicitFiberTargetG: Range | null;
  mealsPerDay: 2 | 3 | 4 | null;
  dietPattern: DietPattern | null;
  avoidFoods: string[];
  allergensMentioned: string[];
  sourceType: "professional_plan" | "self_created" | "unknown";
  extractionConfidence: Confidence;
  uncertainFields: string[];
  proteinBasis: "explicit" | "estimated" | "missing";
  fiberBasis: "explicit" | "estimated" | "missing";
}

export interface NutritionLabelExtraction {
  productName: string;
  servingSizeText: string;
  servingsPerContainer: number | null;
  proteinPerServingG: number;
  fiberPerServingG: number;
  extractionConfidence: Confidence;
  uncertainFields: string[];
}

export interface DailyProgress {
  date: string;
  protein: Range;
  fiber: Range;
  checkedMeals: number;
  target: TargetProfile;
}

export const isRange = (value: unknown): value is Range => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<Range>;
  return typeof candidate.min === "number" && Number.isFinite(candidate.min) && candidate.min >= 0 &&
    typeof candidate.max === "number" && Number.isFinite(candidate.max) && candidate.max >= candidate.min;
};

export const isProfileInput = (value: unknown): value is ProfileInput => {
  if (!value || typeof value !== "object") return false;
  const profile = value as Partial<ProfileInput>;
  return typeof profile.weightKg === "number" && Number.isFinite(profile.weightKg) && profile.weightKg >= 30 && profile.weightKg <= 350 &&
    ["fat_loss", "maintain", "muscle_gain"].includes(String(profile.goalType)) &&
    ["omnivore", "vegetarian", "vegan"].includes(String(profile.dietPattern)) &&
    ["0", "1-2", "3-4", "5+"].includes(String(profile.trainingFrequency)) &&
    [2, 3, 4].includes(Number(profile.mealsPerDay)) && Array.isArray(profile.excludedFoods) &&
    Array.isArray(profile.safetyFlags) && profile.adultConfirmed === true && profile.privacyConsent === true && profile.wellnessConsent === true &&
    typeof profile.timezone === "string" && profile.timezone.length > 0 && profile.timezone.length <= 80 &&
    ["metric", "imperial"].includes(String(profile.unitSystem));
};

export const isMealAnalysis = (value: unknown): value is MealAnalysis => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<MealAnalysis>;
  if (!isRange(item.mealProteinG) || !isRange(item.mealFiberG)) return false;
  if (!(["high", "medium", "low"] as unknown[]).includes(item.confidence)) return false;
  if (!Array.isArray(item.items) || item.items.length > 30) return false;
  return item.items.every((food) => Boolean(food) && typeof food.name === "string" &&
    food.name.length > 0 && food.name.length <= 120 && isRange(food.portionG) &&
    isRange(food.proteinG) && isRange(food.fiberG) && typeof food.confidence === "number" &&
    food.confidence >= 0 && food.confidence <= 1);
};

export const isPlanExtraction = (value: unknown): value is PlanExtraction => {
  if (!value || typeof value !== "object") return false;
  const plan = value as Partial<PlanExtraction>;
  const optionalRange = (range: unknown) => range === null || isRange(range);
  return typeof plan.planTitle === "string" && plan.planTitle.length > 0 && plan.planTitle.length <= 160 &&
    optionalRange(plan.explicitProteinTargetG) && optionalRange(plan.explicitFiberTargetG) &&
    (plan.mealsPerDay === null || [2, 3, 4].includes(Number(plan.mealsPerDay))) &&
    (plan.dietPattern === null || ["omnivore", "vegetarian", "vegan"].includes(String(plan.dietPattern))) &&
    Array.isArray(plan.avoidFoods) && Array.isArray(plan.allergensMentioned) && Array.isArray(plan.uncertainFields) &&
    ["professional_plan", "self_created", "unknown"].includes(String(plan.sourceType)) &&
    ["high", "medium", "low"].includes(String(plan.extractionConfidence)) &&
    ["explicit", "estimated", "missing"].includes(String(plan.proteinBasis)) &&
    ["explicit", "estimated", "missing"].includes(String(plan.fiberBasis));
};

export const isNutritionLabelExtraction = (value: unknown): value is NutritionLabelExtraction => {
  if (!value || typeof value !== "object") return false;
  const label = value as Partial<NutritionLabelExtraction>;
  const bounded = (number: unknown, max: number) => typeof number === "number" && Number.isFinite(number) && number >= 0 && number <= max;
  return typeof label.productName === "string" && label.productName.trim().length > 0 && label.productName.length <= 120 &&
    typeof label.servingSizeText === "string" && label.servingSizeText.trim().length > 0 && label.servingSizeText.length <= 120 &&
    (label.servingsPerContainer === null || (typeof label.servingsPerContainer === "number" && bounded(label.servingsPerContainer, 100) && label.servingsPerContainer >= 0.1)) && bounded(label.proteinPerServingG, 200) &&
    bounded(label.fiberPerServingG, 100) && ["high", "medium", "low"].includes(String(label.extractionConfidence)) &&
    Array.isArray(label.uncertainFields) && label.uncertainFields.length <= 20 &&
    label.uncertainFields.every((field) => typeof field === "string" && field.length <= 120);
};
